import React from 'react';
import { Points } from 'components/Points/Points';
import { SETUP_TOTAL_TASKS } from 'constants/home';
import { OnboardingTask, OnboardingTaskKey } from 'types/interfaces/onboarding';
import AffordabilityLine from './AffordabilityLine';
import { AffordabilityResult } from './affordability';
import { BannerState, BarSegment, ChipStates, SetupTotals } from './setupState';
import { CHIP_HELP, EARNED_FROM_SETUP, HIDE_FOOTNOTE_AFTER, HIDE_FOOTNOTE_BEFORE, STILL_WAITING_FOR_YOU, WAITING_FOR_YOU } from './setupCopy';
import TaskChip from './TaskChip';

// The right column: the remaining total as a large gold figure, claimed and
// ready-to-claim totals, "{n} of 10 done", the segmented bar, every other task
// as a chip, and the affordability line.

interface Props {
  state: BannerState;
  tasks: OnboardingTask[];
  chips: ChipStates;
  sortedKeys: OnboardingTaskKey[];
  segments: BarSegment[];
  totals: SetupTotals;
  featuredKey: OnboardingTaskKey | null;
  addressGated: boolean;
  affordability: AffordabilityResult | null;
  claiming: boolean;
  /** Phone not verified (state A): the claim affordances are hidden, progress stays visible (pass 7) */
  gated?: boolean;
  onPick: (task: OnboardingTask) => void;
  onClaim: (task: OnboardingTask) => void;
}

const SetupLadder: React.FC<Props> = ({ state, tasks, chips, sortedKeys, segments, totals, featuredKey, addressGated, affordability, claiming, gated = false, onPick, onClaim }) => {
  const byKey = new Map(tasks.map((t) => [t.taskKey, t]));
  const everythingDone = totals.doneCount === tasks.length;
  // Review 2026-09-28: the big figure is the points on tasks not finished yet
  const heading = everythingDone ? EARNED_FROM_SETUP : totals.doneCount === 0 ? WAITING_FOR_YOU : STILL_WAITING_FOR_YOU;
  const figure = everythingDone ? totals.claimedPoints : totals.unfinishedPoints;
  const figureNote = everythingDone
    ? state === 'I'
      ? `of ${totals.totalPossible.toLocaleString('en-US')}`
      : `all ${SETUP_TOTAL_TASKS} tasks`
    : `across ${totals.unfinishedCount} ${totals.unfinishedCount === 1 ? 'task' : 'tasks'}`;
  const showChipHelp = state === 'B';
  const showAffordability = !gated && !!affordability && state !== 'H' && state !== 'I';

  return (
    <div className="setup-ladder" data-testid="setup-ladder" data-gated={gated || undefined}>
      <div className="setup-ladder__head">
        <div className="setup-ladder__figure-block">
          <span className="setup-ladder__heading">{heading}</span>
          <div className="setup-ladder__figure-row">
            <Points type="inline" points={figure} className="setup-ladder__figure" />
            <span className="setup-ladder__figure-note">{figureNote}</span>
          </div>
        </div>
        <div className="setup-ladder__tallies">
          <span className="setup-ladder__tally" data-testid="setup-claimed">
            <Points type="inline" points={totals.claimedPoints} /> claimed
          </span>
          {!gated && totals.readyPoints > 0 && (
            <span className="setup-ladder__tally setup-ladder__tally--ready" data-testid="setup-ready">
              <Points type="inline" points={totals.readyPoints} /> ready to claim
            </span>
          )}
          <span className="setup-ladder__tally setup-ladder__done" data-testid="setup-done">
            {totals.doneCount} of {SETUP_TOTAL_TASKS} done
          </span>
        </div>
      </div>

      <div className="setup-bar" role="img" aria-label={`${totals.doneCount} of ${SETUP_TOTAL_TASKS} setup tasks done`} data-testid="setup-bar">
        {segments.map((seg) => (
          <span
            key={seg.key}
            className={`setup-bar__segment setup-bar__segment--${seg.state}${seg.featured ? ' setup-bar__segment--featured' : ''}`}
            style={{ flexGrow: seg.weight }}
            data-key={seg.key}
            data-state={seg.state}
          />
        ))}
      </div>

      <div className="setup-chips" data-testid="setup-chips">
        {sortedKeys
          .filter((key) => key !== featuredKey || chips[key] === 'ready')
          // Gated: a ready chip is a claim the server would refuse
          .filter((key) => !gated || chips[key] !== 'ready')
          .map((key) => {
            const task = byKey.get(key);
            if (!task) return null;
            const chipState = chips[key] ?? 'open';
            return (
              <TaskChip
                key={key}
                task={task}
                state={chipState}
                featured={key === featuredKey}
                needsAddress={addressGated && chipState === 'locked' && (key === 'SEND_FIRST_TRADE' || key === 'COMPLETE_FIRST_TRADE')}
                disabled={claiming && chipState === 'ready'}
                onPick={onPick}
                onClaim={onClaim}
              />
            );
          })}
      </div>

      {showChipHelp && <p className="setup-ladder__help">{CHIP_HELP}</p>}

      {state === 'I' && (
        <p className="setup-ladder__footnote" data-testid="setup-hide-footnote">
          {HIDE_FOOTNOTE_BEFORE}
          <Points type="inline" points={totals.remainingPoints} />
          {HIDE_FOOTNOTE_AFTER}
        </p>
      )}

      {showAffordability && affordability && <AffordabilityLine result={affordability} />}
    </div>
  );
};

export default SetupLadder;
